import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingCart, Package, Check, MapPin, Plus } from "lucide-react";
import { useStore } from "../../context/StoreContext";
import { useUserAuth } from "../../context/UserAuthContext";
import { useCart } from "../../context/CartContext";
import { useResolvedPrice } from "../../hooks/useStoresPricingStock";
import { strikePublicHt, proUnitHt } from "../../utils/resolvedPriceDisplay";

export default function ProductCard({ product, showStock = false, preResolved = null }) {
  const [wishlisted, setWishlisted] = useState(false);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const { selectedStore } = useStore();
  const { user, token } = useUserAuth();
  const { addItem } = useCart();
  const { resolved: fetchedResolved } = useResolvedPrice(preResolved ? null : product?.id, token);
  const resolved = preResolved || fetchedResolved;

  const imageUrl =
    (product.images || []).find(
      (u) => typeof u === "string" && (u.startsWith("http://") || u.startsWith("https://"))
    ) || product.image;
  const catalogPrice = product.price != null ? Number(product.price) : null;

  const isProPending = user?.is_pro && user?.pro_status !== "approved";
  const isApprovedPro = user?.is_pro && user?.pro_status === "approved";
  const priceLabel = isApprovedPro ? "HT" : "TTC";
  const rawResolved = resolved ? Number(resolved.price) : catalogPrice;
  const displayPrice = isApprovedPro ? proUnitHt(resolved, catalogPrice) : rawResolved;

  const isPromoType = resolved?.price_type === "promo_ttc" || resolved?.price_type === "promo";
  const hasVisiblePromo = resolved?.has_visible_promo || isPromoType;
  const strikeHtPro = isApprovedPro ? strikePublicHt(resolved, catalogPrice) : null;
  const promoOldPrice = (() => {
    if (isApprovedPro || !hasVisiblePromo || displayPrice == null) return null;
    let raw = null;
    if (resolved?.price_before_visible_promo != null) raw = Number(resolved.price_before_visible_promo);
    else if (product.oldPrice != null && Number(product.oldPrice) > (rawResolved ?? 0)) raw = Number(product.oldPrice);
    else if (catalogPrice != null && catalogPrice > (rawResolved ?? 0)) raw = catalogPrice;
    if (raw == null) return null;
    return raw;
  })();
  const promoLabel = hasVisiblePromo ? (resolved?.visible_promo_label || "Promo") : null;
  const productUrl = `/produit/${product.slug}`;

  const storeStock = (() => {
    if (!selectedStore || !product.storeStocks) return null;
    const entry = (product.storeStocks || []).find((s) => s.store_id === selectedStore.id);
    return entry ? entry.available : null;
  })();

  const hasMultiStoreData = product.store_stock_total != null;
  const globalStock = hasMultiStoreData
    ? product.store_stock_total
    : product.stock != null
    ? product.stock
    : storeStock != null
    ? storeStock
    : null;
  const inStock = globalStock == null || globalStock > 0;

  const handleAddToCart = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setAdding(true);
      try {
        const ok = await addItem(product, 1, resolved);
        if (ok) { setAdded(true); setTimeout(() => setAdded(false), 2000); }
      } catch {}
      finally { setAdding(false); }
    },
    [product, addItem, resolved]
  );

  const handleWishlist = (e) => { e.preventDefault(); e.stopPropagation(); setWishlisted((v) => !v); };

  const hasOtherStores = !!product?.other_stores_available;
  const depotOnly = (product?.depotAvailable || product?.depot_available) && !inStock;
  const outOfStock = !inStock && !depotOnly && !hasOtherStores;

  const stockBadge = (() => {
    if (storeStock != null) {
      if (storeStock > 0) return { text: `En stock · ${selectedStore?.city || "Agence"}`, cls: "text-emerald-700", dot: "bg-emerald-500", icon: true };
      if (hasOtherStores) return { text: "Dispo dans une autre agence", cls: "text-violet-600", dot: "bg-violet-500", icon: false };
      if (depotOnly) return { text: "Sur commande", cls: "text-blue-600", dot: "bg-blue-500", icon: false };
      return { text: `Indisponible · ${selectedStore?.city || "Agence"}`, cls: "text-red-500", dot: "bg-red-400", icon: true };
    }
    if (inStock) return { text: "Disponible", cls: "text-emerald-700", dot: "bg-emerald-500", icon: false };
    if (depotOnly) return { text: "Sur commande", cls: "text-blue-600", dot: "bg-blue-500", icon: false };
    return { text: "Rupture", cls: "text-red-500", dot: "bg-red-500", icon: false };
  })();

  return (
    <article className="group relative flex flex-col bg-white rounded-xl border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.10)] hover:-translate-y-0.5 transition-all duration-300 ease-out overflow-hidden h-full">
      <Link to={productUrl} className="flex flex-col flex-1">
        {/* Image */}
        <div className="relative bg-gradient-to-b from-[#f8f9fb] to-[#f3f4f6] overflow-hidden">
          <div className="aspect-[4/3] flex items-center justify-center p-5 sm:p-6">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={product.name}
                className="max-w-full max-h-full object-contain group-hover:scale-[1.04] transition-transform duration-500 ease-out"
                loading="lazy"
              />
            ) : (
              <Package className="w-16 h-16 text-gray-200" />
            )}
          </div>

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {isApprovedPro && (
              <span className="px-2.5 py-1 rounded-md text-[9px] font-black bg-[#190276] text-white uppercase tracking-widest shadow-sm">PRO</span>
            )}
            {inStock && (showStock || selectedStore) && storeStock > 0 && (
              <span className="px-2.5 py-1 rounded-md text-[9px] font-bold bg-emerald-500 text-white shadow-sm">En stock</span>
            )}
            {hasVisiblePromo && (
              <span className="px-2.5 py-1 rounded-md text-[9px] font-black bg-red-500 text-white uppercase tracking-widest shadow-sm">{promoLabel || "Promo"}</span>
            )}
            {(product.badges || []).filter((b) => {
              const low = String(b).toLowerCase();
              if (low === "true" || low === "false") return false;
              if (low.includes("promo")) return false;
              return true;
            }).slice(0, 2).map((b, i) => (
              <span key={i} className="px-2.5 py-1 rounded-md text-[9px] font-bold bg-white/95 text-gray-700 shadow-sm border border-gray-200/60 backdrop-blur-sm">{b}</span>
            ))}
          </div>

          {/* Wishlist */}
          <button
            type="button"
            onClick={handleWishlist}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-200 hover:scale-110 z-10 shadow-sm border border-gray-100/50"
            aria-label={wishlisted ? "Retirer des favoris" : "Ajouter aux favoris"}
          >
            <Heart className={`w-4 h-4 transition-colors ${wishlisted ? "fill-red-500 text-red-500" : "text-gray-400"}`} />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 px-4 pt-3.5 pb-2">
          {product.brand && (
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.08em] mb-1">{product.brand}</p>
          )}
          <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 min-h-[2.6em] group-hover:text-[#190276] transition-colors duration-200">
            {product.name}
          </h3>

          {/* Stock */}
          <div className={`flex items-center gap-1.5 mt-2 ${stockBadge.cls}`}>
            {stockBadge.icon ? (
              <MapPin className="w-3 h-3 shrink-0" />
            ) : (
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${stockBadge.dot}`} />
            )}
            <span className="text-[11px] font-semibold">{stockBadge.text}</span>
          </div>

          {/* Price : mobile — barré au-dessus, une seule mention HT/TTC à côté du prix */}
          <div className="mt-auto pt-3">
            {displayPrice != null ? (
              <div className="flex flex-col gap-0.5">
                {isApprovedPro && strikeHtPro != null && strikeHtPro > displayPrice + 0.001 && (
                  <span className="text-[11px] text-gray-400 line-through tabular-nums">
                    {strikeHtPro.toFixed(2).replace(".", ",")} €
                  </span>
                )}
                {!isApprovedPro && promoOldPrice != null && promoOldPrice > displayPrice && (
                  <span className="text-[11px] text-gray-400 line-through tabular-nums">
                    {promoOldPrice.toFixed(2).replace(".", ",")} €
                  </span>
                )}
                <div className="flex items-baseline gap-1.5 flex-wrap">
                  <span className={`text-xl sm:text-[22px] font-black tracking-tight leading-none tabular-nums ${isApprovedPro ? "text-[#190276]" : hasVisiblePromo ? "text-red-600" : "text-gray-900"}`}>
                    {displayPrice.toFixed(2).replace(".", ",")}
                    <span className="text-[13px] sm:text-[15px] font-bold"> €</span>
                  </span>
                  <span className="text-[9px] text-gray-500 font-bold uppercase tracking-wide">{priceLabel}</span>
                </div>
              </div>
            ) : (
              <span className="text-sm font-semibold text-gray-400">Sur demande</span>
            )}
            {isApprovedPro && hasVisiblePromo && (
              <p className="text-[9px] text-emerald-600 mt-0.5 font-medium">Meilleur prix appliqué</p>
            )}
            {isProPending && (
              <p className="text-[9px] text-amber-600 mt-0.5">Tarifs pro après validation</p>
            )}
          </div>
        </div>
      </Link>

      {/* CTA */}
      <div className="px-4 pb-4 pt-2">
        <div className="rounded-lg overflow-hidden">
          {outOfStock ? (
            <div
              className="w-full flex items-center justify-center gap-1.5 h-9 sm:h-11 text-[13px] font-bold bg-gray-100 text-gray-400 cursor-not-allowed"
              aria-label="Indisponible"
            >
              <span>Indisponible</span>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={adding}
              className={`w-full flex items-center justify-center gap-1.5 sm:gap-2 h-9 sm:h-11 text-[13px] font-bold transition-all duration-200 ${
                added
                  ? "bg-emerald-500 text-white shadow-sm"
                  : adding
                  ? "bg-gray-100 text-gray-400"
                  : "bg-[#190276] text-white hover:bg-[#130160] active:scale-[0.97] shadow-sm hover:shadow-md"
              }`}
              aria-label={depotOnly ? "Commander" : "Ajouter au panier"}
            >
              {added ? (
                <><Check className="w-4 h-4" /><span className="hidden sm:inline"> Ajouté !</span></>
              ) : adding ? (
                <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              ) : depotOnly ? (
                <><Package className="w-4 h-4" /><span className="hidden sm:inline"> Sur commande</span></>
              ) : (
                <><Plus className="w-4 h-4 sm:hidden" /><ShoppingCart className="w-4 h-4" /><span className="hidden sm:inline"> Ajouter au panier</span></>
              )}
            </button>
          )}
          <div className="flex h-[3px]"><div className="flex-1 bg-[#190276]" /><div className="flex-1 bg-[#F2B531]" /><div className="flex-1 bg-[#f7040a]" /></div>
        </div>
      </div>
    </article>
  );
}

export function ProductCardSkeleton() {
  return (
    <div className="flex flex-col bg-white rounded-xl border border-gray-100 overflow-hidden h-full">
      <div className="bg-gradient-to-b from-[#f8f9fb] to-[#f3f4f6]">
        <div className="aspect-[4/3] animate-pulse bg-gray-100/40" />
      </div>
      <div className="px-4 pt-3.5 pb-2 space-y-2.5">
        <div className="h-2.5 bg-gray-100 rounded w-1/4 animate-pulse" />
        <div className="h-3.5 bg-gray-100 rounded w-full animate-pulse" />
        <div className="h-3.5 bg-gray-100 rounded w-2/3 animate-pulse" />
        <div className="h-2.5 bg-gray-100 rounded w-1/3 animate-pulse" />
        <div className="h-7 bg-gray-100 rounded w-2/5 animate-pulse mt-2" />
      </div>
      <div className="px-4 pb-4 pt-2">
        <div className="h-11 bg-gray-100 rounded-lg animate-pulse" />
      </div>
    </div>
  );
}
